import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import AuthCallback from "./auth/AuthCallback";
import ProtectedRoute from "./auth/ProtectedRoute";
import Landing from "./pages/Landing";
import Apply from "./pages/Apply";
import Applied from "./pages/Applied";
import Status from "./pages/Status";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Portal from "./pages/Portal";
import Profile from "./pages/Profile";
import Contract from "./pages/Contract";
import CreatorHome from "./pages/CreatorHome";
import CreatorLearning from "./pages/CreatorLearning";
import CreatorLearningModule from "./pages/CreatorLearningModule";
import CreatorDocuments from "./pages/CreatorDocuments";
// 2026-07-24 — Admin routes lazy-loaded so /apply, /, /login and other creator-facing
// pages don't drag the admin bundle down with them. Each admin chunk splits into
// its own webpack file and only loads when an admin actually visits that route.
const AdminOverview               = lazy(() => import("./pages/admin/Overview"));
const AdminContracts              = lazy(() => import("./pages/admin/Contracts"));
const AdminSettings               = lazy(() => import("./pages/admin/Settings"));
const AdminProgress               = lazy(() => import("./pages/admin/AdminProgress"));
const AdminLoginAudit             = lazy(() => import("./pages/admin/AdminLoginAudit"));
const AdminAnalytics              = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminFiling                 = lazy(() => import("./pages/admin/AdminFiling"));
const AdminDocuSignSettings       = lazy(() => import("./pages/admin/AdminDocuSignSettings"));
const AdminForms                  = lazy(() => import("./pages/admin/Forms"));
const AdminLearning               = lazy(() => import("./pages/admin/AdminLearning"));
const AdminDocuments              = lazy(() => import("./pages/admin/AdminDocuments"));
const CreatorDetail               = lazy(() => import("./pages/admin/CreatorDetail"));
const MagazineApplicationDetail   = lazy(() => import("./pages/admin/MagazineApplicationDetail"));

function AdminFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="animate-pulse font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold/50">Loading admin…</div>
    </div>
  );
}

// /signup → /login (Google OAuth handles signup). Keep as a thin 301-style redirect.
function SignupRedirect() {
  useEffect(() => {
    window.location.replace(window.location.origin + "/login?from=signup");
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: synchronous fragment detection during render (NOT in useEffect).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<AdminFallback />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/applied" element={<Applied />} />
      <Route path="/status" element={<Status />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Authenticated */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator"
        element={
          <ProtectedRoute>
            <CreatorHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator/learning"
        element={
          <ProtectedRoute>
            <CreatorLearning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator/learning/:module_id"
        element={
          <ProtectedRoute>
            <CreatorLearningModule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator/documents"
        element={
          <ProtectedRoute>
            <CreatorDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <Portal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract"
        element={
          <ProtectedRoute>
            <Contract />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contracts"
        element={
          <ProtectedRoute>
            <AdminContracts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/progress"
        element={
          <ProtectedRoute>
            <AdminProgress />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/login-audit"
        element={
          <ProtectedRoute>
            <AdminLoginAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/filing"
        element={
          <ProtectedRoute>
            <AdminFiling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/integrations/docusign"
        element={
          <ProtectedRoute>
            <AdminDocuSignSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/forms"
        element={
          <ProtectedRoute>
            <AdminForms />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/learning"
        element={
          <ProtectedRoute>
            <AdminLearning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/documents"
        element={
          <ProtectedRoute>
            <AdminDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/applications/:application_id"
        element={
          <ProtectedRoute>
            <MagazineApplicationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/:user_id"
        element={
          <ProtectedRoute>
            <CreatorDetail />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
